import React, { useState } from 'react';
import type { SlimBannerProps } from '@stitch-fix/mode-react';
import type { HeaderProps } from '@stitch-fix/knit';
import { D } from '@mobily/ts-belt';
import { useAdultsIncentiveBanner } from './adults/useAdultsIncentiveBanner';
import { useKidsIncentiveBanner } from './kids/useKidsIncentiveBanner';
import { SignupIncentiveModal } from '../../components/Layout/SignupIncentiveBanner/SignupIncentiveModal';
import { useAllocation } from '../useAllocation';
import {
  SlimBannerModal,
  type SlimBannerModalProps,
} from '../../components/SlimBanner/SlimBannerModal';
import { shouldRenderSlimBannerModal } from '../../components/SlimBanner/SlimBanner';

// eslint-disable-next-line no-shadow
export enum ModalCampaign {
  TWENTY_DOLLAR,
  PROMO_MESSAGING,
  KIDS_FIRST_FIX,
}

// Experiment:
// https://ab.daylight.stitchfix.com/expts/1611
export const variantExp = {
  expectedValues: ['promo-dark', 'grapefruit', 'forest', 'citrus'],
  fallbackValue: 'promo-dark',
  name: 'eng.incentives.holiday_slim_banner_variant_2025',
};

// Experiment:
// https://ab.daylight.stitchfix.com/expts/1633
export const upperFunnelPromoMessagingExp = {
  expectedValues: ['control', 'treatment'],
  fallbackValue: 'control',
  name: 'eng.onboarding.upper_funnel_promo_messaging',
};

export type UseIncentiveBannerProps = {
  hideHeaderFooter?: boolean;
  customSlimBanner?: HeaderProps['slimBanner'] & {
    modal: Omit<SlimBannerModalProps, 'isOpen' | 'close'>;
  };
};

export const useIncentiveBanner = ({
  hideHeaderFooter = false,
  customSlimBanner = undefined,
}: UseIncentiveBannerProps) => {
  const [showBannerPlaceholder, setShowBannerPlaceholder] = useState(
    !hideHeaderFooter,
  );
  const [showModal, setShowModal] = useState(false);

  const modalClose = () => setShowModal(false);
  const handleBannerClick = () => setShowModal(true);
  const handleBannerLoad = () => setShowBannerPlaceholder(false);

  const { data: allocationVariantData, loading: expLoading } = useAllocation(
    hideHeaderFooter ? [] : [variantExp],
  );

  const { data: allocationPromoData, loading: exp2Loading } = useAllocation(
    hideHeaderFooter ? [] : [upperFunnelPromoMessagingExp],
    { offlineLookupOnly: true },
  );

  // Determine which promo messaging the visitor is allocated to
  const promoMessagingAllocation =
    allocationPromoData?.visitor?.configParams?.find(
      param => param.name === upperFunnelPromoMessagingExp.name,
    );

  const isUpperFunnelPromoMessagingTreatment =
    promoMessagingAllocation?.value === 'treatment';

  // Determine which banner variant/color to show
  const variantAllocation = allocationVariantData?.visitor?.configParams?.find(
    param => param.name === variantExp.name,
  );
  const slimBannerVariant = (variantAllocation?.value ||
    variantExp.fallbackValue) as SlimBannerProps['variant'];

  const { banner: adultBanner, slimBanner: adultSlimBanner } =
    useAdultsIncentiveBanner({
      onClick: handleBannerClick,
      onLoad: handleBannerLoad,
      variant: slimBannerVariant,
      isLoading: expLoading || exp2Loading,
      isUpperFunnelPromoMessagingTreatment,
    });

  const { banner: kidsBanner, slimBanner: kidsSlimBanner } =
    useKidsIncentiveBanner({
      onClick: handleBannerClick,
      onLoad: handleBannerLoad,
      variant: slimBannerVariant,
      isLoading: expLoading,
    });

  let modalCampaign;

  // We ensure adult banners only show on adult pages and kids banners only show on kids pages
  // Therefore, we can use the banner existence to inform which modal campaign to use
  if (adultBanner || adultSlimBanner) {
    modalCampaign = isUpperFunnelPromoMessagingTreatment
      ? ModalCampaign.PROMO_MESSAGING
      : ModalCampaign.TWENTY_DOLLAR;
  } else if (kidsBanner || kidsSlimBanner) {
    modalCampaign = ModalCampaign.KIDS_FIRST_FIX;
  }

  const modal = (
    <SignupIncentiveModal
      isOpen={showModal}
      close={modalClose}
      modalCampaign={modalCampaign}
    />
  );

  const { modal: customModalProps, ..._customSlimBannerProps } =
    customSlimBanner || {};

  const customSlimBannerProps = D.isEmpty(_customSlimBannerProps)
    ? undefined
    : (_customSlimBannerProps as HeaderProps['slimBanner']);

  const customModal =
    customModalProps && shouldRenderSlimBannerModal(customModalProps) ? (
      <SlimBannerModal
        isOpen={showModal}
        close={modalClose}
        heading={customModalProps.heading}
        body={customModalProps.body}
        image={customModalProps.image}
        cta={customModalProps.cta}
      />
    ) : undefined;

  // We ensure adult banners only show on adult pages and kids banners only show on kids pages
  // Therefore, we can use this || logic in the banner and slimBanner return values
  return {
    showBannerPlaceholder,
    banner: adultBanner || kidsBanner,
    slimBanner: adultSlimBanner || kidsSlimBanner || customSlimBannerProps,
    modal: modalCampaign ? modal : customModal,
  };
};
